import { InfiniteData, infiniteQueryOptions, UseInfiniteQueryOptions } from "@tanstack/react-query";
import { Annotation, Build, CursorPaginated, Job } from "~/components/build/Show/lib/types";
import fetcher from "~/components/build/Show/lib/fetcher";
import { totalAnnotationCount } from "~/components/build/Show/components/Sidebar/TreeNode/AnnotationsBadge";

export const jobAnnotationsQueryKey = (buildId: Build["id"], job: Job) => {
  return [
    "build",
    buildId,
    "jobs",
    job.id,
    "annotations",
    `count:${totalAnnotationCount(job.statistics.annotationCountsByStyle || {})}`,
  ];
};

type JobAnnotationsPage = CursorPaginated<Annotation>;
type JobAnnotationsPageParam = string | null;
type JobAnnotationsQueryKey = ReturnType<typeof jobAnnotationsQueryKey>;

type AdditionalOptions = Omit<
  UseInfiniteQueryOptions<
    JobAnnotationsPage,
    Error,
    InfiniteData<JobAnnotationsPage, JobAnnotationsPageParam>,
    JobAnnotationsQueryKey,
    JobAnnotationsPageParam
  >,
  "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam"
>;

export const jobAnnotationsQueryOptions = (build: Build, job: Job, options?: AdditionalOptions) => {
  return infiniteQueryOptions({
    ...options,
    queryKey: jobAnnotationsQueryKey(build.id, job),
    queryFn: ({ pageParam, signal }) =>
      fetchJobAnnotations(build.buildDataBasePath, job.id, pageParam, signal),
    initialPageParam: null as JobAnnotationsPageParam,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : null),
  });
};

function fetchJobAnnotations(
  buildDataBasePath: Build["buildDataBasePath"],
  jobId: Job["id"],
  pageParam: string | null,
  signal: AbortSignal,
) {
  const url = new URL(`${buildDataBasePath}/jobs/${jobId}/annotations`, window.location.origin);

  if (pageParam) {
    url.searchParams.set("after", pageParam);
  }

  return fetcher<CursorPaginated<Annotation>>(url, true, { signal });
}
